import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Slide,
  SlideProps,
  Box,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { NotificationData } from '@/types';
import { notificationService } from '@/services/notificationService';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export const NotificationContainer: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [expandedNotifications, setExpandedNotifications] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleNotificationsChange = (newNotifications: NotificationData[]) => {
      setNotifications(newNotifications);
    };

    notificationService.addNotificationListener(handleNotificationsChange);

    return () => {
      notificationService.removeNotificationListener(handleNotificationsChange);
    };
  }, []);

  const handleClose = (notificationId: string) => {
    notificationService.removeNotification(notificationId);
  };

  const handleExpand = (notificationId: string) => {
    setExpandedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const getSeverity = (type: NotificationData['type']) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  };


  if (notifications.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        maxWidth: 400,
        width: '100%',
      }}
    >
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration || 6000}
          onClose={() => handleClose(notification.id)}
          TransitionComponent={SlideTransition}
          sx={{ mb: 1 }}
        >
          <Alert
            severity={getSeverity(notification.type)}
            onClose={() => handleClose(notification.id)}
            sx={{
              width: '100%',
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {notification.message.length > 100 && (
                  <IconButton
                    size="small"
                    onClick={() => handleExpand(notification.id)}
                    sx={{ ml: 1 }}
                  >
                    {expandedNotifications.has(notification.id) ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => handleClose(notification.id)}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            }
          >
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {notification.title}
              </Typography>
              <Collapse in={expandedNotifications.has(notification.id) || notification.message.length <= 100}>
                <Typography variant="body2">
                  {notification.message}
                </Typography>
              </Collapse>
              {notification.message.length > 100 && !expandedNotifications.has(notification.id) && (
                <Typography variant="body2" color="text.secondary">
                  {notification.message.substring(0, 100)}...
                </Typography>
              )}
            </Box>
          </Alert>
        </Snackbar>
      ))}
    </Box>
  );
};
